import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { PawPrint, FileUp, Package, Settings, LogOut, Store, Newspaper, MessageSquareQuote, Users, BarChart3, FileDown, AlertCircle, ArrowRight, HeartHandshake } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/store/Logo";
import { PdfUploader } from "@/components/admin/PdfUploader";
import { ProductsManager } from "@/components/admin/ProductsManager";
import { SettingsPanel } from "@/components/admin/SettingsPanel";
import { BlogManager } from "@/components/admin/BlogManager";
import { ReviewsManager } from "@/components/admin/ReviewsManager";
import { OrdersManager } from "@/components/admin/OrdersManager";
import { AnalyticsPanel } from "@/components/admin/AnalyticsPanel";
import { CatalogExport } from "@/components/admin/CatalogExport";
import { CloseFriendsManager } from "@/components/admin/CloseFriendsManager";

const TABS = [
  { id: "orders", label: "Pedidos", icon: Users },
  { id: "analytics", label: "Analítica", icon: BarChart3 },
  { id: "closefriends", label: "Close Friends", icon: HeartHandshake },
  { id: "pdf", label: "Cargar catálogo", icon: FileUp },
  { id: "products", label: "Productos", icon: Package },
  { id: "catalog", label: "Catálogo PDF", icon: FileDown },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "reviews", label: "Reseñas", icon: MessageSquareQuote },
  { id: "settings", label: "Ajustes", icon: Settings },
];

const noPhoto = (p) => !((p.images && p.images.length > 0) || (p.image_url || "").trim());

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [refreshKey, setRefreshKey] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [productsView, setProductsView] = useState("published");

  const loadPending = useCallback(async () => {
    try {
      const { data } = await api.get("/products", { params: { include_all: true } });
      setPendingCount(data.filter(noPhoto).length);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { loadPending(); }, [loadPending, refreshKey]);

  const goToPending = () => { setProductsView("pending"); setTab("products"); setRefreshKey((k) => k + 1); };

  return (
    <div className="min-h-screen paw-bg">
      <header className="sticky top-0 z-30 bg-cream/90 backdrop-blur-md border-b border-[#E3E7E2]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Logo size={40} />
          <div className="leading-none">
            <p className="font-display text-lg font-bold text-charcoal">Panel de administración</p>
            <p className="text-xs text-muted-moss">{user?.email}</p>
          </div>
          <div className="flex-1" />
          <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-[#E3E7E2] text-sm font-semibold text-charcoal hover:bg-sage/40">
            <Store className="w-4 h-4" /> Ver tienda
          </Link>
          <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border border-[#E3E7E2] text-sm font-semibold text-charcoal hover:text-destructive">
            <LogOut className="w-4 h-4" /> Salir
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        {pendingCount > 0 && !dismissed && (
          <div data-testid="pending-photo-alert" className="mb-4 rounded-2xl bg-peach border border-coral/40 p-4 flex items-center gap-3">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-coral/20 text-coral shrink-0"><AlertCircle className="w-5 h-5" /></span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-charcoal">{pendingCount} producto{pendingCount > 1 ? "s" : ""} sin publicar esperando una foto</p>
              <p className="text-xs text-muted-moss">Agrega una foto para que aparezcan en tu tienda.</p>
            </div>
            <button data-testid="pending-alert-cta" onClick={goToPending} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-coral text-white text-sm font-semibold hover:bg-coral-dark active:scale-95 transition-all shrink-0">
              Revisar <ArrowRight className="w-4 h-4" />
            </button>
            <button data-testid="pending-alert-dismiss" onClick={() => setDismissed(true)} aria-label="Descartar" className="text-muted-moss hover:text-charcoal text-lg leading-none px-1 shrink-0">×</button>
          </div>
        )}

        <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-none">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`relative inline-flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all border ${
                tab === t.id ? "bg-coral text-white border-coral card-shadow" : "bg-white text-charcoal border-[#E3E7E2] hover:bg-peach/50"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
              {t.id === "products" && pendingCount > 0 && (
                <span data-testid="products-pending-badge" className={`ml-1 min-w-[20px] h-5 px-1.5 grid place-items-center rounded-full text-[11px] font-bold ${tab === "products" ? "bg-white text-coral" : "bg-coral text-white"}`}>{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "orders" && <OrdersManager />}
        {tab === "analytics" && <AnalyticsPanel />}
        {tab === "closefriends" && <CloseFriendsManager />}
        {tab === "pdf" && (
          <div>
            <div className="bg-teal/25 border border-teal/50 rounded-2xl p-4 mb-5 flex items-start gap-3">
              <PawPrint className="w-5 h-5 text-coral shrink-0 mt-0.5" />
              <p className="text-sm text-charcoal">Sube tu catálogo en PDF o fotos de productos y la IA extraerá automáticamente los datos, precios y fotos. Después podrás revisarlos antes de publicarlos.</p>
            </div>
            <PdfUploader onSaved={() => setRefreshKey((k) => k + 1)} />
          </div>
        )}
        {tab === "products" && <ProductsManager key={refreshKey} initialView={productsView} onChange={loadPending} />}
        {tab === "catalog" && <CatalogExport />}
        {tab === "blog" && <BlogManager />}
        {tab === "reviews" && <ReviewsManager />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}
